import { Check, ChevronDown, ChevronUp, Plus, RefreshCw, Trash2, WandSparkles } from "lucide-react";
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { toast } from "sonner";



import Dropdown from "../../lib/ui/components/dropdown/Dropdown";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import GenerateQuestionsModal, { QUESTION_GENERATION_TYPE } from "./GenerateQuestionsModal";
import { getModalConfig } from "../ConfirmationModal/modalHelper";

import { UPDATE_INTERVIEW_QUESTIONS, REGENERATE_INTERVIEW_QUESTIONS, FETCH_CANDIDATE_INTERVIEWS, GENERATE_SCORING_RUBRIC } from "../../lib/urls";
import { copyAllQuestionsHandler, exportToPDFHandler } from "./ExportInterviewQuestionsHelper";

import { PASS_FAIL_SCORES, SCORES, emptyPassFailScoring, emptyScoring } from "./analysisRubricDraft";
import "./InterviewQuestions.css";
import { useStore } from "../../store";
import { BodyText, Heading6, LabelText, XsmallText, SmallText } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Toggle from "../../lib/ui/components/toggle/Toggle";
import VisibilityHint from "./VisibilityHint";
import CollapsibleContent from "../../lib/ui/components/collapsible-content/CollapsibleContent";
import SectionFooter from "../../lib/ui/components/section-footer/SectionFooter";
import Spinner from "../../lib/ui/components/spinner/Spinner";

const detectInterviewStatus = (applicants, statusFilter) => {
  if (!Array.isArray(applicants) || applicants.length === 0) return false;
  return applicants.some(
    (applicant) => applicant?.interview_session?.status === statusFilter
  );
};

const normalizeQuestion = (question) => {
  const rubric = question?.scoring_rubric;
  const isPassFail = question?.is_pass_fail ?? rubric?.type === "pass_fail";
  return {
    ...question,
    id: question?.id ?? question?.question_id ?? null,
    is_pass_fail: Boolean(isPassFail),
    scoring: question?.scoring ?? (rubric?.type === "scored" ? rubric.levels : emptyScoring()),
    pass_fail_scoring: question?.pass_fail_scoring ?? (rubric?.type === "pass_fail" ? rubric.levels : emptyPassFailScoring()),
  };
};

const QUESTION_TYPE_STORAGE_KEY = "june_question_generation_type";

function getSavedQuestionType() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(QUESTION_TYPE_STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

function saveQuestionType(type) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(QUESTION_TYPE_STORAGE_KEY, type);
  } catch (_) {}
}

function clearSavedQuestionType() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(QUESTION_TYPE_STORAGE_KEY);
  } catch (_) {}
}

const SCORE_BADGE_CLASSES = {
  5: "bg-green-100 text-green-800",
  4: "bg-emerald-50 text-green-700",
  3: "bg-yellow-100 text-yellow-800",
  2: "bg-orange-100 text-orange-700",
  1: "bg-red-100 text-red-700",
  pass: "bg-green-100 text-green-800",
  fail: "bg-red-100 text-red-700",
};

const SCORE_TITLE_LABELS = {
  5: "Exceptional",
  4: "Great",
  3: "Qualified",
  2: "Below expectations",
  1: "Clearly below requirements",
  pass: "Pass",
  fail: "Fail",
};

const SCORE_PLACEHOLDERS = {
  pass: "What a passing answer looks like",
  fail: "What a failing answer looks like",
};

const scoringFor = (question) =>
  question.is_pass_fail
    ? { key: "pass_fail_scoring", scores: PASS_FAIL_SCORES, values: question.pass_fail_scoring ?? emptyPassFailScoring() }
    : { key: "scoring", scores: SCORES, values: question.scoring ?? emptyScoring() };

const legacyDesiredResponse = (question) =>
  question.scoring_rubric ? null : question.desired_answer || question.desired_responses || null;

const INTERVIEW_QUESTION_MAX_CHARS = 1000;
const MAX_INTERVIEW_QUESTIONS = 50;
const GENERATION_FAILED_MESSAGE = "Failed to regenerate interview questions. Please try again.";
let nextLocalQuestionKey = 0;
const withLocalKey = (question) => ({
  ...question,
  __localQuestionKey: question.__localQuestionKey ?? `local-${++nextLocalQuestionKey}`,
});
const withoutLocalKey = ({ __localQuestionKey, ...question }) => question;
const questionIdentity = (question) => question.id ?? question.__localQuestionKey;
const isUntouchedNewQuestion = (question) => {
  if (question.id || question.question.trim()) return false;
  const { scores, values } = scoringFor(question);
  return scores.every((score) => !String(values[score] || "").trim());
};
// New knockouts go before the first scored question so they are still asked
// first; new scored questions go at the end.
const knockoutInsertIndex = (questions) => {
  const firstScored = questions.findIndex((question) => !question.is_pass_fail);
  return firstScored === -1 ? questions.length : firstScored;
};

const withScoringRubric = (question, rubric) => ({
  ...question,
  scoring_rubric: rubric,
  [rubric.type === "pass_fail" ? "pass_fail_scoring" : "scoring"]: rubric.levels,
});

function InterviewQuestions({ interviewQuestions, jobId, companyId, roleName, companyName, onQuestionsUpdate, onJobUpdate, applicants, onCandidatesUpdate, onDirtyStateChange, onHasQuestionsChange, saveRef, jobDescription, footerHost, onStartInterviewing, onBack, setupWizard = true }) {
  const { token } = useStore();

  const [draftQuestions, setDraftQuestions] = useState(() => {
    const questions = Array.isArray(interviewQuestions) ? interviewQuestions : [];
    return questions.map((question) => withLocalKey(normalizeQuestion(question)));
  });
  // Same snapshot as the draft. A second withLocalKey() pass would mint new
  // keys and look like unsaved edits before the user changes anything.
  const [lastSavedState, setLastSavedState] = useState(() =>
    JSON.stringify(draftQuestions)
  );
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [generatingRubricIndex, setGeneratingRubricIndex] = useState(null);
  const [isGeneratingLegacyRubrics, setIsGeneratingLegacyRubrics] = useState(false);
  const [expandedQuestionIndexes, setExpandedQuestionIndexes] = useState(() => new Set());
  const [passFailConfirmOpen, setPassFailConfirmOpen] = useState(false);
  const [pendingPassFailIndex, setPendingPassFailIndex] = useState(null);
  const [generateQuestionsModalOpen, setGenerateQuestionsModalOpen] = useState(false);
  const [questionGenerationType, setQuestionGenerationType] = useState(
    () => getSavedQuestionType() || QUESTION_GENERATION_TYPE.KNOCKOUT_PLUS_EVALUATION
  );
  const [alwaysUseSetting, setAlwaysUseSetting] = useState(false);
  const lastPropRef = useRef();

  const ensureApplicantsRef = useRef();

  useEffect(() => {
    const questionsArray = Array.isArray(interviewQuestions) ? interviewQuestions : [];
    // Ensure each question has is_pass_fail field, default to false if missing
    const normalizedQuestions = questionsArray.map(normalizeQuestion);
    const newQuestionsString = JSON.stringify(normalizedQuestions);

    if (lastPropRef.current !== newQuestionsString) {
      lastPropRef.current = newQuestionsString;
      const keyedQuestions = normalizedQuestions.map(withLocalKey);
      setDraftQuestions(keyedQuestions);
      setLastSavedState(JSON.stringify(keyedQuestions));
      setExpandedQuestionIndexes((current) => new Set(
        [...current].filter((index) => index < normalizedQuestions.length)
      ));
    }
  }, [interviewQuestions]);

  const savedQuestionText = useMemo(() => new Map(
    JSON.parse(lastSavedState).map((question) => [questionIdentity(question), question.question])
  ), [lastSavedState]);
  const isInvalidLength = useCallback((question) =>
    question.question.length > INTERVIEW_QUESTION_MAX_CHARS &&
    question.question !== savedQuestionText.get(questionIdentity(question)),
  [savedQuestionText]);
  const hasInvalidLength = draftQuestions.some(isInvalidLength);

  const isSaveEnabled = useMemo(() => {
    if (!Array.isArray(draftQuestions)) return false;

    // If there are no questions at all, disable the button
    if (draftQuestions.length === 0) return false;

    // Require the question text
    const hasEmptyQuestions = draftQuestions.some(
      q => q.question.trim() === ""
    );

    if (hasEmptyQuestions || hasInvalidLength) return false;

    const hasIncompleteRubric = draftQuestions.some((question) => {
      const { scores, values } = scoringFor(question);
      const hasAnyRubric = scores.some((score) => String(values[score] || "").trim());
      if (!hasAnyRubric) return !question.desired_answer && !question.desired_responses;
      return scores.some((score) => !String(values[score] || "").trim());
    });
    if (hasIncompleteRubric) return false;

    // check if current state is different from last saved state
    const currentState = JSON.stringify(draftQuestions);
    return currentState !== lastSavedState;
  }, [draftQuestions, lastSavedState, hasInvalidLength]);

  const hasUnsavedChanges = useMemo(() => {
    if (!Array.isArray(draftQuestions) || draftQuestions.length === 0) {
      return false;
    }
    // Local keys exist only in the editor and must not count as edits, and a
    // question added but never filled in has nothing to lose.
    const comparable = (questions) => JSON.stringify(
      questions.filter((question) => !isUntouchedNewQuestion(question)).map(withoutLocalKey)
    );
    return comparable(draftQuestions) !== comparable(JSON.parse(lastSavedState));
  }, [draftQuestions, lastSavedState]);

  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onDirtyStateChange]);

  const applySavedQuestions = useCallback((savedQuestions, showToast = true, refreshJob = true) => {
    const list = (savedQuestions || []).map((question) => withLocalKey(normalizeQuestion(question)));
    if (onQuestionsUpdate) onQuestionsUpdate(list.map(withoutLocalKey));
    setDraftQuestions(list);
    setLastSavedState(JSON.stringify(list));
    if (refreshJob && onJobUpdate) onJobUpdate();
    if (showToast) toast.success("Interview questions updated successfully");
    return list;
  }, [onQuestionsUpdate, onJobUpdate]);

  const saveQuestions = useCallback(async ({ showSuccessToast = true, refreshJob = true } = {}) => {
    if (draftQuestions.some(isInvalidLength)) {
      throw new Error(`Interview questions must be ${INTERVIEW_QUESTION_MAX_CHARS} characters or fewer.`);
    }
    const response = await fetch(
      `${UPDATE_INTERVIEW_QUESTIONS(companyId, jobId)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ interview_questions: draftQuestions.map(withoutLocalKey) }),
      }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        response.status === 400 && data.detail
          ? data.detail
          : "Failed to update interview questions."
      );
    }

    let savedQuestions = [];
    if (Array.isArray(data.interview_questions)) {
      savedQuestions = data.interview_questions;
    } else if (typeof data.interview_questions === "string") {
      try {
        savedQuestions = JSON.parse(data.interview_questions);
      } catch (_) {
        savedQuestions = [];
      }
    }

    return applySavedQuestions(savedQuestions, showSuccessToast, refreshJob);
  }, [companyId, jobId, token, draftQuestions, applySavedQuestions, isInvalidLength]);

  const updateQuestionHandler = useCallback(async () => {
    const hasInProgress = await ensureApplicantsRef.current("Saving question changes", { showToast: false });
    if (hasInProgress) {
      const proceed = window.confirm(
        "Some interviews are currently in progress. Changing questions may affect ongoing interviews.\n\nDo you want to continue?"
      );
      if (!proceed) {
        return;
      }
    }
    setIsUpdating(true);
    try {
      await saveQuestions();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdating(false);
    }
  }, [saveQuestions]);

  const requestGenerateRef = useRef(() => {});
  // Bumped on discard so a generation still in flight doesn't add its
  // questions back into the draft the employer just threw away.
  const discardCountRef = useRef(0);

  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        save: () => {
          updateQuestionHandler();
        },
        discard: () => {
          discardCountRef.current += 1;
          setDraftQuestions(JSON.parse(lastSavedState));
        },
        requestGenerate: () => requestGenerateRef.current(),
      };
    }
  }, [saveRef, lastSavedState, updateQuestionHandler]);

  // Local applicants cache to ensure immediate availability for warnings
  const [applicantsCache, setApplicantsCache] = useState(() => Array.isArray(applicants) ? applicants : []);
  useEffect(() => {
    setApplicantsCache(Array.isArray(applicants) ? applicants : []);
  }, [applicants]);

  const fetchAllCandidatesLocally = async () => {
    if (!companyId || !jobId) return [];
    try {
      const response = await fetch(FETCH_CANDIDATE_INTERVIEWS(companyId, jobId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      const normalized = Array.isArray(data) ? data : [];
      setApplicantsCache(normalized);
      return normalized;
    } catch (e) {
      return [];
    }
  };

  const ensureApplicantsLoadedAndWarn = async (actionLabel, options = {}) => {
    const { showToast = true } = options;
    let currentApplicants = applicantsCache;
    if (!Array.isArray(currentApplicants) || currentApplicants.length === 0) {
      try {
        // Ask parent to refresh (may update props on next render)
        if (onCandidatesUpdate) {
          await onCandidatesUpdate(true);
        }
      } catch (_) {
        // ignore parent fetch errors
      }
      // Fetch locally to use immediately
      currentApplicants = await fetchAllCandidatesLocally();
    }
    const hasInProgress = detectInterviewStatus(currentApplicants, "IN_PROGRESS");
    if (hasInProgress && showToast) {
      toast.warning(`Warning: Some interviews are in progress. ${actionLabel} may affect ongoing interviews.`);
    }
    return hasInProgress;
  };

  useEffect(() => {
    ensureApplicantsRef.current = ensureApplicantsLoadedAndWarn;
  });

  // Generation reads the question from the server, so unsaved edits are saved first.
  const saveBeforeGenerating = async ({ force = false } = {}) => {
    if (draftQuestions.some(isInvalidLength)) {
      throw new Error(`Interview questions must be ${INTERVIEW_QUESTION_MAX_CHARS} characters or fewer.`);
    }
    if (!force && JSON.stringify(draftQuestions) === lastSavedState) return draftQuestions;
    setIsUpdating(true);
    try {
      return await saveQuestions({ showSuccessToast: false, refreshJob: false });
    } finally {
      setIsUpdating(false);
    }
  };

  const requestScoringRubric = async (questionId) => {
    const response = await fetch(
      GENERATE_SCORING_RUBRIC(companyId, jobId, questionId),
      { method: "POST", headers: { Authorization: `Bearer ${token}` } }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || "Could not generate the Scoring Rubric");
    return data.scoring_rubric;
  };

  const generateScoringRubric = async (index) => {
    if (!draftQuestions[index]?.question?.trim()) {
      toast.error("Add a question before generating its Scoring Rubric.");
      return;
    }
    setGeneratingRubricIndex(index);
    try {
      const questionsToUse = await saveBeforeGenerating({ force: !draftQuestions[index].id });

      const question = questionsToUse[index];
      if (!question?.id) {
        throw new Error("Could not save the question before generating its Scoring Rubric.");
      }

      const rubric = await requestScoringRubric(question.id);
      const newQuestions = [...questionsToUse];
      newQuestions[index] = withScoringRubric(newQuestions[index], rubric);
      applySavedQuestions(newQuestions, false);
      toast.success("Scoring Rubric generated");
    } catch (error) {
      toast.error(error.message || "Could not generate the Scoring Rubric");
    } finally {
      setGeneratingRubricIndex(null);
    }
  };

  const legacyQuestionCount = draftQuestions.filter(legacyDesiredResponse).length;

  const generateLegacyScoringRubrics = async () => {
    setIsGeneratingLegacyRubrics(true);
    try {
      const questionsToUse = await saveBeforeGenerating();
      const legacyIndexes = questionsToUse.flatMap((question, index) =>
        legacyDesiredResponse(question) && question.id ? [index] : []
      );
      const results = await Promise.allSettled(
        legacyIndexes.map((index) => requestScoringRubric(questionsToUse[index].id))
      );
      const newQuestions = [...questionsToUse];
      results.forEach((result, n) => {
        if (result.status === "fulfilled") {
          newQuestions[legacyIndexes[n]] = withScoringRubric(newQuestions[legacyIndexes[n]], result.value);
        }
      });
      applySavedQuestions(newQuestions, false);

      const failed = results.filter((result) => result.status === "rejected").length;
      if (failed) {
        toast.error(`${failed} of ${results.length} Scoring Rubrics could not be generated. Try again to retry the rest.`);
      } else {
        toast.success(`Generated ${results.length} Scoring Rubric${results.length === 1 ? "" : "s"}`);
      }
    } catch (error) {
      toast.error(error.message || "Could not generate the Scoring Rubrics");
    } finally {
      setIsGeneratingLegacyRubrics(false);
    }
  };

  const isGeneratingRubric = generatingRubricIndex !== null || isGeneratingLegacyRubrics;

  const handleScoringChange = (index, score, text) => {
    const newQuestions = [...draftQuestions];
    const { key, values } = scoringFor(newQuestions[index]);
    const levels = { ...values, [score]: text };
    newQuestions[index] = {
      ...newQuestions[index],
      [key]: levels,
      scoring_rubric: {
        type: newQuestions[index].is_pass_fail ? "pass_fail" : "scored",
        levels,
      },
    };
    setDraftQuestions(newQuestions);
  };

  const handleChange = (index, type, e) => {
    if (type === "question") {
      const value = e.target.value;
      setDraftQuestions((current) => current.map((question, questionIndex) =>
        questionIndex === index ? { ...question, [type]: value } : question
      ));
    }
  };

  const handleMoveQuestion = async (currentIndex, targetIndex) => {
    await ensureApplicantsLoadedAndWarn("Reordering questions");
    const newQuestions = [...draftQuestions]
    newQuestions.splice(targetIndex, 0, newQuestions.splice(currentIndex, 1)[0]);
    setDraftQuestions(newQuestions);
    setExpandedQuestionIndexes((current) => new Set(
      [...current].map((index) => {
        if (index === currentIndex) return targetIndex;
        if (currentIndex < targetIndex && index > currentIndex && index <= targetIndex) {
          return index - 1;
        }
        if (currentIndex > targetIndex && index >= targetIndex && index < currentIndex) {
          return index + 1;
        }
        return index;
      })
    ));
  }

  const toggleQuestionExpanded = (index) => {
    setExpandedQuestionIndexes((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handlePassFailToggle = (index) => {
    const currentValue = draftQuestions[index].is_pass_fail || false;

    if (!currentValue) {
      setPendingPassFailIndex(index);
      setPassFailConfirmOpen(true);
    } else {
      const newQuestions = [...draftQuestions];
      newQuestions[index] = {
        ...newQuestions[index],
        is_pass_fail: false,
        scoring_rubric: null,
        scoring: emptyScoring(),
      };
      setDraftQuestions(newQuestions);
    }
  };

  const confirmPassFailToggle = () => {
    const index = pendingPassFailIndex;
    setPassFailConfirmOpen(false);
    setPendingPassFailIndex(null);
    if (index == null) return;

    const newQuestions = [...draftQuestions];
    newQuestions[index] = {
      ...newQuestions[index],
      is_pass_fail: true,
      scoring_rubric: null,
      pass_fail_scoring: emptyPassFailScoring(),
    };
    setDraftQuestions(newQuestions);
  };

  const addNewQuestionHandler = () => {
    const newQuestions = [...draftQuestions];
    newQuestions.push(withLocalKey({ question: "", scoring: emptyScoring(), pass_fail_scoring: emptyPassFailScoring(), is_pass_fail: false }));
    setDraftQuestions(newQuestions);
    setExpandedQuestionIndexes((current) => new Set(current).add(newQuestions.length - 1));

    setTimeout(() => {
      const questionElements = document.querySelectorAll('.inter-question');
      if (questionElements.length > 0) {
        const lastQuestion = questionElements[questionElements.length - 1];
        lastQuestion.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const deleteQuestionHandler = async (index) => {
    await ensureApplicantsLoadedAndWarn("Deleting a question");
    const newQuestions = [...draftQuestions];
    newQuestions.splice(index, 1);
    setDraftQuestions(newQuestions);
    setExpandedQuestionIndexes((current) => new Set(
      [...current]
        .filter((expandedIndex) => expandedIndex !== index)
        .map((expandedIndex) => expandedIndex > index ? expandedIndex - 1 : expandedIndex)
    ));
  };

  const regenerateQuestionsHandler = async (questionType) => {
    if (!jobId || !companyId) {
      toast.error("Missing job or company information");
      return;
    }
    if (!jobDescription || !jobDescription.trim()) {
      toast.error("Add a job description before generating interview questions.");
      return;
    }
    const room = MAX_INTERVIEW_QUESTIONS - draftQuestions.length;
    if (room <= 0) {
      toast.error(`Interviews can have up to ${MAX_INTERVIEW_QUESTIONS} questions.`);
      return;
    }
    // Generation adds to the draft without saving, so the backend sees the
    // questions on screen, not the saved ones.
    const currentQuestions = draftQuestions
      .filter((question) => question.question.trim())
      .map((question) => ({
        question: question.question.trim().slice(0, INTERVIEW_QUESTION_MAX_CHARS),
        is_pass_fail: question.is_pass_fail,
      }));
    const discardCount = discardCountRef.current;
    const wasDiscarded = () => discardCountRef.current !== discardCount;
    setIsRegenerating(true);
    try {
      const response = await fetch(
        REGENERATE_INTERVIEW_QUESTIONS(companyId, jobId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ question_type: questionType, current_questions: currentQuestions }),
        }
      );
      if (!response.ok) {
        // Only a 400 carries a message written for the employer.
        const data = await response.json().catch(() => ({}));
        if (!wasDiscarded()) {
          toast.error(response.status === 400 && data.detail ? data.detail : GENERATION_FAILED_MESSAGE);
        }
        return;
      }
      const data = await response.json();
      if (wasDiscarded()) return;
      let list = [];
      if (Array.isArray(data.interview_questions)) {
        list = data.interview_questions;
      } else if (typeof data.interview_questions === "string") {
        try {
          list = JSON.parse(data.interview_questions);
        } catch (_) { }
      }
      const generated = list
        .slice(0, room)
        .map((question) => withLocalKey(normalizeQuestion(question)));
      const knockouts = generated.filter((question) => question.is_pass_fail);
      const scored = generated.filter((question) => !question.is_pass_fail);
      // The list is hidden while generating and a discard drops the result,
      // so the draft is the one this request was made from.
      const insertAt = knockoutInsertIndex(draftQuestions);
      setDraftQuestions((current) => [
        ...current.slice(0, insertAt),
        ...knockouts,
        ...current.slice(insertAt),
        ...scored,
      ]);
      setExpandedQuestionIndexes((current) => new Set(
        [...current].map((index) => index >= insertAt ? index + knockouts.length : index)
      ));
      const limitNote = generated.length < list.length
        ? ` The interview is now at the ${MAX_INTERVIEW_QUESTIONS}-question limit.`
        : "";
      toast.success(
        `Added ${generated.length} interview question${generated.length === 1 ? "" : "s"}. Save to keep them.${limitNote}`
      );
    } catch (error) {
      if (!wasDiscarded()) {
        toast.error(GENERATION_FAILED_MESSAGE);
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  requestGenerateRef.current = () => {
    // Evaluation Rules can start generation too, so the button's disabled
    // state alone doesn't prevent a second request.
    if (isRegenerating) return;
    const savedType = getSavedQuestionType();
    if (savedType) {
      regenerateQuestionsHandler(savedType);
      return;
    }
    setAlwaysUseSetting(false);
    setGenerateQuestionsModalOpen(true);
  };

  const confirmGenerateQuestions = () => {
    setGenerateQuestionsModalOpen(false);
    if (alwaysUseSetting) {
      saveQuestionType(questionGenerationType);
    } else {
      clearSavedQuestionType();
    }
    regenerateQuestionsHandler(questionGenerationType);
  };

  // Wrapper functions to call the helper functions with the required parameters
  const handleCopyAllQuestions = () => {
    copyAllQuestionsHandler(draftQuestions, roleName, companyName);
  };

  const handleExportToPDF = async () => {
    await exportToPDFHandler(draftQuestions, roleName, companyName);
  };

  const hasExistingQuestions = draftQuestions.some(
    (question) => Boolean(question.id) || Boolean((question.question || "").trim())
  );
  // Generated questions stay unsaved, so the parent can't tell from the saved
  // list whether the draft already has questions.
  useEffect(() => {
    onHasQuestionsChange?.(hasExistingQuestions);
  }, [hasExistingQuestions, onHasQuestionsChange]);
  const pinFooter = footerHost !== undefined;
  const isAtQuestionLimit = draftQuestions.length >= MAX_INTERVIEW_QUESTIONS;
  const generateQuestionsLabel = isRegenerating
    ? "Generating..."
    : hasExistingQuestions
      ? "Generate More Interview Questions"
      : "Generate Interview Questions";
  const generateQuestionsButton = (
    <Button
      onClick={() => requestGenerateRef.current()}
      disabled={isRegenerating || isAtQuestionLimit}
      variant="tertiary"
      data-cy="regenerate-interview-questions-button"
      leftIcon={isRegenerating ? <RefreshCw size="1em" /> : <WandSparkles size="1em" />}
    >
      {generateQuestionsLabel}
    </Button>
  );
  const saveQuestionsButton = (
    <Button
      variant={pinFooter && setupWizard ? "secondary" : "primary"}
      onClick={updateQuestionHandler}
      data-cy="update-interview-questions-button"
      leftIcon={<Check size="1em" />}
      disabled={!isSaveEnabled || isUpdating || isStarting}
    >
      {isUpdating ? "Saving..." : "Save Questions"}
    </Button>
  );
  const hasEmptyQuestion = draftQuestions.some((question) => !question.question.trim());
  const canStartInterviewing = hasExistingQuestions
    && !hasEmptyQuestion
    && !hasInvalidLength
    && !isRegenerating
    && !isUpdating
    && !isStarting
    && (!hasUnsavedChanges || isSaveEnabled);
  const startInterviewing = async () => {
    if (!canStartInterviewing) return;
    setIsStarting(true);
    try {
      if (isSaveEnabled) {
        await saveQuestions({ showSuccessToast: false });
      }
      await onStartInterviewing?.();
    } catch (error) {
      toast.error(error.message || "Could not start interviewing");
    } finally {
      setIsStarting(false);
    }
  };
  const startInterviewingButton = (
    <Button
      variant="primary"
      onClick={startInterviewing}
      disabled={!canStartInterviewing}
      data-cy="complete-role-setup-button"
    >
      {isStarting ? "Completing..." : isSaveEnabled ? "Save & Complete Role Setup" : "Complete Role Setup"}
    </Button>
  );

  return (
    <div className="interview-questions-container">
      <div className="mb-4"></div>
      <div className="flex items-center mb-2">
        <LabelText weight="normal">Interview Questions</LabelText>
        <div className="flex space-x-2 ml-auto">
          {!pinFooter && generateQuestionsButton}
          <Button
            data-cy="add-question-button"
            onClick={addNewQuestionHandler}
            variant="secondary"
            leftIcon={<Plus size="1em" />}
            disabled={isUpdating || isRegenerating || isAtQuestionLimit}
          >
            Add Questions
          </Button>
          {!pinFooter && saveQuestionsButton}
        </div>
        <div className="ml-2">
          <Dropdown
            dataCyTrigger="interview-questions-dropdown-trigger"
            items={[
              {
                label: "Copy All Questions",
                onClick: handleCopyAllQuestions,
                dataCy: "copy-all-questions-dropdown"
              },
              {
                label: "Export to PDF",
                onClick: handleExportToPDF,
                dataCy: "export-pdf-dropdown"
              }
            ]}
          />
        </div>
      </div>
      {isAtQuestionLimit && (
        <BodyText className="text-right" data-cy="interview-question-limit-note">
          Interviews can have up to {MAX_INTERVIEW_QUESTIONS} questions.
        </BodyText>
      )}

      {legacyQuestionCount > 0 && (
        <div
          className="mt-4 flex items-center gap-4 rounded-md border border-amber-200 bg-amber-50 p-3"
          data-cy="legacy-scoring-rubrics-banner"
        >
          <div className="flex-1">
            <XsmallText emphasis={true} weight="semibold">
              {legacyQuestionCount === 1
                ? "1 question uses a legacy desired response"
                : `${legacyQuestionCount} questions use a legacy desired response`}
            </XsmallText>
            <XsmallText>
              Generate Scoring Rubrics to migrate them. The desired response stays as the fallback until a rubric exists.
            </XsmallText>
          </div>
          <Button
            onClick={generateLegacyScoringRubrics}
            disabled={isGeneratingRubric || isUpdating || isRegenerating}
            variant="secondary"
            leftIcon={isGeneratingLegacyRubrics ? <RefreshCw size="1em" /> : <WandSparkles size="1em" />}
            data-cy="generate-legacy-scoring-rubrics-button"
          >
            {isGeneratingLegacyRubrics ? "Generating..." : "Generate Scoring Rubrics"}
          </Button>
        </div>
      )}

      <div className="mt-[24px]">
        <SmallText weight="bold" emphasis={true} className="leading-[150%]">
          Review and edit the 'knockout' questions below.
        </SmallText>
        <SmallText className="leading-[150%]">
          These should be the top few questions that will screen out applicants
          that you would not consider for the next round.
        </SmallText>
      </div>

      <div className="mt-6">
        {isRegenerating ? (
          <div
            className="flex flex-col items-center rounded-md border border-gray-200 bg-white px-4 py-16 text-center"
            data-cy="interview-questions-loading"
          >
            <Spinner size="xl" />
            <Heading6 emphasis={false} className="mt-4">Generating interview questions</Heading6>
          </div>
        ) : draftQuestions?.length === 0 ? (
          <div className="text-center py-8">
            <Heading6 emphasis={false}>No Interview Questions have been added.</Heading6>
          </div>
        ) : (
          <div className="interview-questions">
            {draftQuestions?.map((question, index) => {
              const isExpanded = expandedQuestionIndexes.has(index);
              const rubricScoring = scoringFor(question);
              const legacyResponse = legacyDesiredResponse(question);
              return (
                <div
                  key={questionIdentity(question)}
                  className="inter-question w-full border border-gray-300 p-4 mb-4 rounded-md relative"
                >
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-[10px]">
                      <div className="flex items-center gap-2">
                        <XsmallText emphasis={true} weight="semibold">Interview Question {index + 1}</XsmallText>
                        <VisibilityHint label="Question text is sent to candidates" />
                      </div>
                      <div className="flex items-center gap-2">
                        <XsmallText>Pass/Fail Question</XsmallText>
                        <Toggle
                          checked={question.is_pass_fail || false}
                          onChange={() => handlePassFailToggle(index)}
                        />
                      </div>
                    </div>
                    <textarea
                      value={question.question}
                      data-cy={`interview-question-input-${index}`}
                      className="w-full bg-gray-50 placeholder:text-slate-400 text-body leading-[150%] text-gray-600 border border-sky-300 rounded-md px-3 py-2 transition duration-300 ease focus:outline-none focus:border-sky-400 hover:border-sky-300 shadow-sm focus:shadow disabled:opacity-60 disabled:cursor-not-allowed"
                      placeholder="Your Question..."
                      onChange={(e) => handleChange(index, "question", e)}
                      rows={3}
                    />
                    <div data-cy={`question-character-count-${index}`} className="text-xs text-slate-500">
                      {question.question.length} / {INTERVIEW_QUESTION_MAX_CHARS}
                    </div>
                    {isInvalidLength(question) && (
                      <div data-cy={`question-length-error-${index}`} className="text-xs text-red-600">
                        Shorten this question to {INTERVIEW_QUESTION_MAX_CHARS} characters or fewer before saving.
                      </div>
                    )}
                    {question.question.length > INTERVIEW_QUESTION_MAX_CHARS && !isInvalidLength(question) && (
                      <div data-cy={`question-length-grandfathered-${index}`} className="text-xs text-slate-500">
                        This existing question exceeds the new limit and can remain unchanged.
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <Button
                      variant="secondary"
                      onClick={() => toggleQuestionExpanded(index)}
                      rightIcon={isExpanded ? <ChevronUp size="1em" /> : <ChevronDown size="1em" />}
                      aria-expanded={isExpanded}
                      aria-controls={`interview-question-details-${index}`}
                      aria-label={`${isExpanded ? "Hide" : "Show"} scoring guide for interview question ${index + 1}`}
                      data-cy={`interview-question-collapse-toggle-${index}`}
                    >
                      {isExpanded ? "Hide Scoring Guide" : "Show Scoring Guide"}
                    </Button>
                    <div className="flex items-center">
                      <Dropdown
                        triggerText='Move Question'
                        items={draftQuestions.map((_, targetIndex) => ({
                          label: `Move to position ${targetIndex + 1}`,
                          onClick: () => handleMoveQuestion(index, targetIndex),
                          disabled: index === targetIndex ? true : false
                        }))}
                      />
                      <Button
                        onClick={() => deleteQuestionHandler(index)}
                        data-cy={`delete-interview-question-button-${index}`}
                        variant="secondary"
                        leftIcon={<Trash2 size="1em" />}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  <CollapsibleContent isOpen={isExpanded} id={`interview-question-details-${index}`}>
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <div className="w-full mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <XsmallText emphasis={true} weight="semibold">Scoring Guide</XsmallText>
                            <XsmallText>
                              {question.is_pass_fail
                                ? "What the evaluator counts as a pass or a fail"
                                : "What an answer at each score looks like"}
                            </XsmallText>
                            <button
                              type="button"
                              onClick={() => generateScoringRubric(index)}
                              disabled={isGeneratingRubric || !question.question.trim() || hasInvalidLength}
                              className="ml-auto text-body text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                              data-cy={`generate-scoring-rubric-${index}`}
                            >
                              {generatingRubricIndex === index ? "Generating…" : "Generate"}
                            </button>
                          </div>
                          <div className="flex flex-col gap-2">
                            {rubricScoring.scores.map((score) => (
                              <div
                                key={score}
                                className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3"
                              >
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`flex items-center justify-center shrink-0 w-7 h-7 rounded-md text-body font-bold ${SCORE_BADGE_CLASSES[score]}`}
                                  >
                                    {question.is_pass_fail ? (score === "pass" ? "P" : "F") : score}
                                  </span>
                                  <span className="text-small font-semibold text-gray-900">
                                    {SCORE_TITLE_LABELS[score]}
                                  </span>
                                </div>
                                <textarea
                                  value={rubricScoring.values[score] ?? ""}
                                  onChange={(e) => handleScoringChange(index, score, e.target.value)}
                                  data-cy={`question-scoring-input-${index}-${score}`}
                                  aria-label={`What a ${SCORE_TITLE_LABELS[score] ?? score} answer looks like`}
                                  placeholder={SCORE_PLACEHOLDERS[score] ?? `What a ${score} looks like`}
                                  rows={4}
                                  className="w-full text-body leading-[150%] text-gray-600 bg-white border border-slate-200 rounded-md p-2 resize-none focus:outline-none focus:border-slate-400 hover:border-slate-300"
                                />
                              </div>
                            ))}
                          </div>
                          {legacyResponse && (
                            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                              <XsmallText emphasis={true} weight="semibold">Legacy desired response</XsmallText>
                              <XsmallText>{legacyResponse}</XsmallText>
                              <div className="mt-2 flex items-center justify-between gap-3">
                                <XsmallText>Generate a Scoring Rubric to migrate this question. The existing text remains the fallback until then.</XsmallText>
                                <Button
                                  type="button"
                                  onClick={() => generateScoringRubric(index)}
                                  disabled={isGeneratingRubric || !question.question.trim()}
                                  variant="secondary"
                                  data-cy={`generate-legacy-scoring-rubric-${index}`}
                                >
                                  {generatingRubricIndex === index ? "Generating…" : "Generate Scoring Rubric"}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {pinFooter && (
        <SectionFooter
          host={footerHost}
          leading={
            <Button
              variant="ghost"
              onClick={onBack}
              disabled={isRegenerating || isUpdating || isStarting}
              data-cy="interview-questions-back-button"
            >
              Back
            </Button>
          }
        >
          {generateQuestionsButton}
          {saveQuestionsButton}
          {setupWizard && startInterviewingButton}
        </SectionFooter>
      )}
      <ConfirmationModal
        isOpen={passFailConfirmOpen}
        onClose={() => {
          setPassFailConfirmOpen(false);
          setPendingPassFailIndex(null);
        }}
        onConfirm={confirmPassFailToggle}
        title="Pass/Fail Question"
        details="Pass/Fail questions are used for requirements that a candidate must meet to move forward in the process. If a candidate fails any Pass/Fail question, they cannot advance to the next stage."
        confirmButtonText="Mark as Pass/Fail"
        confirmButtonVariant="primary"
      />
      <ConfirmationModal
        isOpen={generateQuestionsModalOpen}
        onClose={() => setGenerateQuestionsModalOpen(false)}
        onConfirm={confirmGenerateQuestions}
        {...getModalConfig("generateQuestions")}
        details={
          <GenerateQuestionsModal
            value={questionGenerationType}
            onChange={setQuestionGenerationType}
          />
        }
        footerChildren={
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={alwaysUseSetting}
              onChange={(e) => setAlwaysUseSetting(e.target.checked)}
              className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
              data-cy="generate-questions-always-use"
            />
            <XsmallText>Always use this setting</XsmallText>
          </label>
        }
        confirmButtonVariant="primary"
        isLoading={isRegenerating}
        loadingText="Generating..."
      />
    </div>
  );
}

export default InterviewQuestions;
